/**
 * Smart Forestry USB tokeni — brauzerning WebAuthn (FIDO2) interfeysi.
 *
 * Token USB portga ulanadi, brauzer uni o'zi taniydi: qo'shimcha dastur yoki drayver kerak
 * emas. Maxfiy kalit tokendan chiqmaydi; PIN faqat brauzer/token oynasida kiritiladi.
 * Server bilan almashinuv JSON (base64url) ko'rinishida: `apps/accounts/services/webauthn_service.py`.
 */

type Json = Record<string, unknown>;

function fromB64u(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function toB64u(buffer: ArrayBuffer | null): string | null {
  if (!buffer) return null;
  let binary = "";
  new Uint8Array(buffer).forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Brauzer WebAuthn'ni qo'llaydimi (HTTPS yoki localhost kerak). */
export function usbTokenSupported(): boolean {
  return typeof window !== "undefined" && window.isSecureContext && "PublicKeyCredential" in window;
}

interface Descriptor {
  id: string;
  type: "public-key";
  transports?: AuthenticatorTransport[];
}

function descriptors(list: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(list) || list.length === 0) return undefined;
  return (list as Descriptor[]).map((item) => ({ ...item, id: fromB64u(item.id) }));
}

/** Yangi tokenni ro'yxatdan o'tkazish: server parametrlari → token → server tekshiradigan javob. */
export async function createCredential(options: Json): Promise<Json> {
  const user = options.user as { id: string; name: string; displayName: string };
  const credential = (await navigator.credentials.create({
    publicKey: {
      ...(options as unknown as PublicKeyCredentialCreationOptions),
      challenge: fromB64u(options.challenge as string),
      user: { ...user, id: fromB64u(user.id) },
      excludeCredentials: descriptors(options.excludeCredentials),
    },
  })) as PublicKeyCredential | null;
  if (!credential) throw new DOMException("Bekor qilindi", "NotAllowedError");
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: toB64u(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: toB64u(response.clientDataJSON),
      attestationObject: toB64u(response.attestationObject),
      transports: response.getTransports?.() ?? [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

/** Kirish: server parametrlari → token (PIN bilan) → server tekshiradigan imzo. */
export async function getAssertion(options: Json): Promise<Json> {
  const credential = (await navigator.credentials.get({
    publicKey: {
      ...(options as unknown as PublicKeyCredentialRequestOptions),
      challenge: fromB64u(options.challenge as string),
      allowCredentials: descriptors(options.allowCredentials),
    },
  })) as PublicKeyCredential | null;
  if (!credential) throw new DOMException("Bekor qilindi", "NotAllowedError");
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: toB64u(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: toB64u(response.clientDataJSON),
      authenticatorData: toB64u(response.authenticatorData),
      signature: toB64u(response.signature),
      userHandle: toB64u(response.userHandle),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

/** Brauzer istisnosini foydalanuvchiga tushunarli i18n kalitiga o'giradi. */
export function tokenErrorKey(error: unknown): string {
  const name = error instanceof DOMException ? error.name : "";
  if (name === "NotAllowedError" || name === "AbortError") return "token.err.cancelled";
  if (name === "InvalidStateError") return "token.err.exists";
  if (name === "SecurityError") return "token.err.security";
  if (name === "NotSupportedError") return "token.err.unsupported";
  return "token.err.generic";
}
